import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import LanguagePdfUploadField from '@ui/components/LanguagePdfUploadField';
import { centeredRowStack } from '@ui/style/style';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getNextVersionAsync } from '@application/features/usageManual/usageManualThunk';
import { USAGE_MANUAL_LANGUAGES } from '../constants';
const UploadUsageManualDialog = ({ open, onClose, onUpload, uploading = false, mode = 'upload', lockedLanguage, displayVersion, existingDocument }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const visibleLanguages = useMemo(() => lockedLanguage
        ? USAGE_MANUAL_LANGUAGES.filter((l) => l.id === lockedLanguage)
        : USAGE_MANUAL_LANGUAGES, [lockedLanguage]);
    const [languageFiles, setLanguageFiles] = useState({});
    const nextVersion = useAppSelector((state) => state.usageManual.nextVersion);
    useEffect(() => {
        if (open) {
            dispatch(getNextVersionAsync());
            setLanguageFiles({});
        }
    }, [open, dispatch]);
    const handleClose = () => {
        setLanguageFiles({});
        onClose();
    };
    const filesToUpload = useMemo(() => visibleLanguages
        .filter((l) => languageFiles[l.id]?.[0])
        .map((l) => ({ file: languageFiles[l.id][0], language: l.id })), [languageFiles, visibleLanguages]);
    const handleUpload = () => {
        if (filesToUpload.length === 0) {
            toast.error(String(t('help.usageManual.fileRequired')));
            return;
        }
        onUpload(filesToUpload);
        handleClose();
    };
    const dialogTitle = String(mode === 'edit' ? t('help.usageManual.editTitle') : t('help.usageManual.uploadTitle'));
    const dialogContent = (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, p: isSmallScreen ? 2 : 3 }, children: [_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('help.usageManual.uploadLanguagesDescription', {
                    defaultValue: 'Please upload the usage manual PDF for each language:'
                }) }), _jsx(LanguagePdfUploadField, { id: 'usage-manual-upload', languages: visibleLanguages, filesByLanguage: languageFiles, onLanguageFilesChange: setLanguageFiles, disabled: uploading, maxFileSizeMb: 10, existingFilesByLanguage: lockedLanguage && existingDocument ? { [lockedLanguage]: existingDocument } : undefined }), mode !== 'edit' && (_jsx(Typography, { variant: 'body2', color: 'text.secondary', textAlign: 'center', children: t('help.usageManual.newVersionNote', { version: displayVersion ?? nextVersion }) }))] }));
    const actionButtons = (_jsxs(Box, { sx: { ...centeredRowStack }, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: handleClose, size: 'large', disabled: uploading, children: t('actions.close') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', onClick: handleUpload, size: 'large', disabled: filesToUpload.length === 0 || uploading, startIcon: uploading ? _jsx(CircularProgress, { size: 16, color: 'inherit' }) : undefined, sx: {
                    backgroundColor: theme.palette.grey[800],
                    '&:hover': { backgroundColor: theme.palette.grey[700] }
                }, children: t('help.usageManual.uploadNow') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: handleClose, title: dialogTitle, dialogActions: actionButtons, children: dialogContent }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleClose, titleWithIcon: dialogTitle, titleIcon: '/assets/images/manual.png', backgroundVariant: 'white', actions: actionButtons, maxWidth: 'sm', children: dialogContent }));
};
export default UploadUsageManualDialog;
